import React from 'react';
import MuiAutocomplete, {
   autocompleteClasses,
   AutocompleteProps as MuiAutocompleteProps,
} from '@mui/material/Autocomplete';
import TextField from 'components/inputs/TextField';
import {DropdownObjectOption} from 'components/inputs/Dropdown';
import {find} from 'lodash';

import useMediaQuery from '@mui/material/useMediaQuery';
import ListSubheader from '@mui/material/ListSubheader';
import Popper from '@mui/material/Popper';
import {useTheme, styled} from '@mui/material/styles';
import {VariableSizeList, ListChildComponentProps} from 'react-window';
import Typography from '@mui/material/Typography';
import {useController, useFormContext} from 'react-hook-form';
import {SetValueOptions} from 'components/hook-form/helpers';
import {useRegisterLabel} from 'common/context/FormContext';

const LISTBOX_PADDING = 8; // px

function renderRow(props: ListChildComponentProps) {
   const {data, index, style} = props;
   const dataSet = data[index];
   //    console.debug('renderRow', data, dataSet);
   const inlineStyle = {
      ...style,
      top: (style.top as number) + LISTBOX_PADDING,
   };

   if (dataSet.hasOwnProperty('group')) {
      return (
         <ListSubheader key={dataSet.key} component="div" style={inlineStyle}>
            {dataSet.group}
         </ListSubheader>
      );
   }

   return (
      <Typography component="li" {...dataSet[0]} noWrap style={inlineStyle}>
         {dataSet[1]}
      </Typography>
   );
}

const OuterElementContext = React.createContext({});

const OuterElementType = React.forwardRef<HTMLDivElement>((props, ref) => {
   const outerProps = React.useContext(OuterElementContext);
   return <div ref={ref} {...props} {...outerProps} />;
});

function useResetCache(data: any) {
   const ref = React.useRef<VariableSizeList>(null);
   React.useEffect(() => {
      if (ref.current != null) {
         ref.current.resetAfterIndex(0, true);
      }
   }, [data]);
   return ref;
}

// Adapter for react-window
const ListboxComponent = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLElement>>(
   function ListboxComponent(props, ref) {
      const {children, ...other} = props;
      const itemData: React.ReactChild[] = [];
      (children as React.ReactChild[]).forEach(
         (item: React.ReactChild & {children?: React.ReactChild[]}) => {
            itemData.push(item);
            itemData.push(...(item.children || []));
         }
      );

      const theme = useTheme();
      const smUp = useMediaQuery(theme.breakpoints.up('sm'), {
         noSsr: true,
      });
      const itemCount = itemData.length;
      const itemSize = smUp ? 36 : 48;

      const getChildSize = (child: React.ReactChild) => {
         if (child.hasOwnProperty('group')) {
            return 48;
         }

         return itemSize;
      };

      const getHeight = () => {
         if (itemCount > 8) {
            return 8 * itemSize;
         }
         return itemData.map(getChildSize).reduce((a, b) => a + b, 0);
      };

      const gridRef = useResetCache(itemCount);

      return (
         <div ref={ref}>
            <OuterElementContext.Provider value={other}>
               <VariableSizeList
                  itemData={itemData}
                  height={getHeight() + 2 * LISTBOX_PADDING}
                  width="100%"
                  ref={gridRef}
                  outerElementType={OuterElementType}
                  innerElementType="ul"
                  itemSize={(index) => getChildSize(itemData[index])}
                  overscanCount={5}
                  itemCount={itemCount}
               >
                  {renderRow}
               </VariableSizeList>
            </OuterElementContext.Provider>
         </div>
      );
   }
);

const StyledPopper = styled(Popper)({
   [`& .${autocompleteClasses.listbox}`]: {
      boxSizing: 'border-box',
      '& ul': {
         padding: 0,
         margin: 0,
      },
   },
});

export type AutocompleteProps = {
   name: string;
   label: string;
   options: DropdownObjectOption[];
   onChange?: MuiAutocompleteProps<DropdownObjectOption, false, false, false>['onChange'];
   disabled?: boolean;
};

type FormAutocompleteProps = AutocompleteProps & {
   value: string;
   setValue: (val: string | null) => void;
   error: string;
};
const Autocomplete = React.memo(
   ({options, name, label, disabled, error, onChange, value, setValue}: FormAutocompleteProps) => {
      return (
         <MuiAutocomplete
            disabled={disabled}
            options={options || []}
            getOptionLabel={(option) => {
               return (
                  (option as DropdownObjectOption)?.name ||
                  (find(options, (opt) => opt.id === ((option as unknown) as string))
                     ?.name as string) ||
                  '<None>'
               );
            }}
            value={(value as unknown) as DropdownObjectOption}
            onChange={(ev, val, reason, details) => {
               onChange && onChange(ev, val, reason, details);
               if (!val) {
                  return setValue(null);
               }
               setValue((val as DropdownObjectOption).id as string);
               // setInnerValue(val);
               // console.debug(ev, val);
               // field.onChange(ev);
            }}
            isOptionEqualToValue={(opt, val) =>
               ((opt as DropdownObjectOption).id as string) === ((val as unknown) as string)
            }
            renderInput={(params) => (
               <TextField
                  {...params}
                  error={!!error}
                  helperText={error}
                  name={name}
                  label={label}
               />
            )}
            //   sx={{ width: 300 }}
            disableListWrap
            PopperComponent={StyledPopper}
            ListboxComponent={ListboxComponent}
            //  groupBy={(option) => option.name.toUpperCase()}
            renderOption={(props, option) => [props, option.name]}
            //  renderTags={(val) => val}
            //  renderGroup={(params) => params}
         />
      );
   }
);

const HookAutocomplete = (props: AutocompleteProps) => {
   const {
      field: {value},
      fieldState: {error},
   } = useController({name: props.name});
   useRegisterLabel(props.name, props.label);
   const {setValue} = useFormContext();

   const _setValue = React.useCallback(
      (val: string | null) => setValue(props.name, val, SetValueOptions),
      [props.name, setValue]
   );
   return (
      <Autocomplete {...props} value={value} setValue={_setValue} error={error?.message || ''} />
   );
};

export const FormAutocomplete = HookAutocomplete;
